import React from 'react';
import { Link } from 'react-router-dom';

const PLANS = [
  {
    id: '1month',
    name: '1 Month',
    reports: 500,
    perReport: 10,
    total: 5000,
    monthlyEquiv: 5000,
    savings: 0,
    highlight: false,
    badge: null,
  },
  {
    id: '3months',
    name: '3 Months',
    reports: 1500,
    perReport: 9,
    total: 13500,
    monthlyEquiv: 4500,
    savings: 10,
    highlight: false,
    badge: null,
  },
  {
    id: '6months',
    name: '6 Months',
    reports: 3000,
    perReport: 8,
    total: 24000,
    monthlyEquiv: 4000,
    savings: 20,
    highlight: true,
    badge: 'Best Value',
  },
  {
    id: 'yearly',
    name: 'Yearly',
    reports: 6000,
    perReport: 7,
    total: 42000,
    monthlyEquiv: 3500,
    savings: 30,
    highlight: false,
    badge: 'Max Savings',
  },
];

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const PricingPage = () => {
  const contactEmail = 'rayoraenterprise@gmail.com';
  const mailtoLink = `mailto:${contactEmail}?subject=White-Label%20Plan%20Inquiry&body=Hi%2C%20I%27m%20interested%20in%20a%20white-label%20plan%20for%20my%20clinic.%20Please%20share%20more%20details.`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-800 hover:text-gray-600 transition-colors">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
          <div className="text-lg font-bold text-gray-900">my-skin.ai</div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-6">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            White-Label Plans
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
            AI Skin Analysis for Your Clinic
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Get your own branded skin analysis portal with AI-powered reports, patient management, and more. Choose a plan that fits your practice.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="pb-20 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl p-6 flex flex-col ${
                plan.highlight
                  ? 'bg-gray-900 text-white ring-2 ring-gray-900 scale-[1.02]'
                  : 'bg-white border border-gray-200 hover:border-gray-300'
              } transition-all duration-200`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-bold ${
                  plan.highlight
                    ? 'bg-green-400 text-gray-900'
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {plan.badge}
                </div>
              )}

              {/* Plan Name */}
              <h3 className={`text-lg font-semibold mb-1 ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                {plan.name}
              </h3>

              {/* Total Price */}
              <div className="mb-4">
                <span className={`text-3xl font-bold ${plan.highlight ? 'text-white' : 'text-gray-900'}`}>
                  {formatCurrency(plan.total)}
                </span>
                <span className={`text-sm ml-1 ${plan.highlight ? 'text-gray-300' : 'text-gray-500'}`}>
                  total
                </span>
              </div>

              {/* Monthly Equivalent */}
              <div className={`text-sm mb-6 ${plan.highlight ? 'text-gray-300' : 'text-gray-500'}`}>
                {formatCurrency(plan.monthlyEquiv)}/month
              </div>

              {/* Divider */}
              <div className={`border-t mb-6 ${plan.highlight ? 'border-gray-700' : 'border-gray-100'}`} />

              {/* Features */}
              <div className="space-y-3 flex-1">
                <Feature highlight={plan.highlight}>
                  <strong>{plan.reports.toLocaleString('en-IN')}</strong> AI reports
                </Feature>
                <Feature highlight={plan.highlight}>
                  {formatCurrency(plan.perReport)} per report
                </Feature>
                <Feature highlight={plan.highlight}>
                  Custom branded portal
                </Feature>
                <Feature highlight={plan.highlight}>
                  Patient analytics dashboard
                </Feature>
                <Feature highlight={plan.highlight}>
                  PDF report generation
                </Feature>
                {plan.savings > 0 && (
                  <Feature highlight={plan.highlight}>
                    <span className={plan.highlight ? 'text-green-400 font-semibold' : 'text-green-600 font-semibold'}>
                      Save {plan.savings}%
                    </span>{' '}
                    vs monthly
                  </Feature>
                )}
              </div>

              {/* CTA */}
              <a
                href={`${mailtoLink}%0A%0APlan%3A%20${encodeURIComponent(plan.name)}`}
                className={`mt-6 block text-center py-3 px-4 rounded-xl text-sm font-semibold transition-all ${
                  plan.highlight
                    ? 'bg-white text-gray-900 hover:bg-gray-100'
                    : 'bg-gray-900 text-white hover:bg-gray-800'
                }`}
              >
                Contact Us
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ / Info */}
      <section className="pb-20 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-10">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">1. Contact Us</h3>
              <p className="text-sm text-gray-600">Reach out via email to discuss your clinic's needs and choose a plan.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">2. Get Set Up</h3>
              <p className="text-sm text-gray-600">We create your branded portal with your clinic's logo, colors, and details.</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">3. Start Growing</h3>
              <p className="text-sm text-gray-600">Share your portal link with patients and track engagement in real-time.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="pb-20 px-4">
        <div className="max-w-4xl mx-auto bg-gray-900 rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to modernize your clinic?
          </h2>
          <p className="text-gray-400 mb-8 max-w-lg mx-auto">
            Get in touch with our team to discuss your requirements and get started with a white-label plan.
          </p>
          <a
            href={mailtoLink}
            className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Contact Us
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-100 py-8 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-sm text-gray-400">
            2026 © my-skin.ai • Powered by Rayora Enterprise
          </div>
        </div>
      </footer>
    </div>
  );
};

const Feature = ({ children, highlight }) => (
  <div className="flex items-start gap-2">
    <svg className={`w-4 h-4 mt-0.5 flex-shrink-0 ${highlight ? 'text-green-400' : 'text-green-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
    <span className={`text-sm ${highlight ? 'text-gray-300' : 'text-gray-600'}`}>{children}</span>
  </div>
);

export default PricingPage;
